import { getState, subscribe } from '@/store/identity-store.js';

export interface ApiRequestAuthority {
  assertCurrent(token?: string | null): void;
  release(): void;
}

function subject(token: string | null): string | null {
  try {
    const payload = token?.split('.')[1];
    if (!payload) return null;
    const value = JSON.parse(
      atob(payload.replace(/-/g, '+').replace(/_/g, '/')),
    ) as { sub?: unknown };
    return typeof value.sub === 'string' && value.sub.length > 0
      ? value.sub
      : null;
  } catch {
    return null;
  }
}

/** Keep private request continuations within their initiating account and UI ownership. */
export function captureApiRequestAuthority(
  isOwned: () => boolean = () => true,
): ApiRequestAuthority {
  const initial = getState();
  const actor = initial.identity?.userId;
  const tenant = initial.identity?.tenantId;
  let invalidated = false;
  const matches = () => {
    const state = getState();
    return Boolean(
      actor &&
      !state.stale &&
      state.identity?.userId === actor &&
      state.identity?.tenantId === tenant &&
      subject(state.platformJwt) === actor,
    );
  };
  if (!matches() || !isOwned())
    throw new Error(
      'Your account or generation context changed. Reopen generation before continuing.',
    );
  const unsubscribe = subscribe(() => {
    if (!matches()) invalidated = true;
  });
  return {
    assertCurrent(token) {
      if (
        invalidated ||
        !matches() ||
        !isOwned() ||
        (token !== undefined && subject(token) !== actor)
      )
        throw new Error(
          'Your account or generation context changed. Reopen generation before continuing.',
        );
    },
    release() {
      invalidated = true;
      unsubscribe();
    },
  };
}
